import { useEffect, useState } from "react";
import NxtbnModal from "../Modal";
import { InputField, NXForm } from "../common";
import useApi from "../../api";
import { toast } from "react-toastify";
import moment from "moment";
import { gql, useLazyQuery } from '@apollo/client';


const GET_ORDER_TOTAL_PRICE = gql`
  query GetOrderDetails($alias: UUID!) {
    order(alias: $alias) {
      totalPriceWithoutSymbol
    }
  }
`;


interface PaymentFormProps {
  order: any;
  payment_method: any;
  isOpen: any;
  onClose: () => void;
  fetchOrderDetails: () => void;
}

function PaymentForm({
  order,
  payment_method,
  isOpen,
  onClose,
  fetchOrderDetails,
}: PaymentFormProps) {
  const [formData, setFormData] = useState<any>();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [forceIt, setForceIt] = useState(false);
  const [errorData, setErrorData] = useState<any>();

  const [getOrderTotalPrice, { data, loading, error, refetch }] = useLazyQuery(GET_ORDER_TOTAL_PRICE);

  useEffect(() => {
    if (order?.alias) {
      getOrderTotalPrice({ variables: { alias: order?.alias } });
    }
  }, []);
  

  useEffect(() => {
    setFormData((prev: any) => ({
      ...prev,
      order: order?.alias,
      payment_method: payment_method,
      payment_amount: data?.order?.totalPriceWithoutSymbol,
    }));
  }, [data]);


  const api = useApi();

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
    
    if (name === "payment_amount") {
      if (value < data?.order?.totalPriceWithoutSymbol) {
        setErrorData({
          payment_amount: ["Payment amount must be equal to the total price."],
        });
      }

      if (value > data?.order?.totalPriceWithoutSymbol) {
        setErrorData({
          payment_amount: ["Payment amount must be equal to the total price."],
        });
      }

      if (value == data?.order?.totalPriceWithoutSymbol) {
        setErrorData(null);
      }

    }

  };

  const handleSelect = (e: any, field: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: e.value,
    }));
  };

  const toggleAdvancedFields = () => {
    setShowAdvanced((prev) => !prev);
  };

  const handleCheckboxChange = (e: any) => {
    setForceIt(e.target.checked);
    setFormData((prev: any) => ({
      ...prev,
      force_it: e.target.checked,
    }));
  };

  const onSubmit = (e: any) => {
    e.preventDefault();
    api.createPayment(formData).then(
      (response: any) => {
        toast.success("Payment Successfully Created.");
        setFormData(null);
        onClose();
        fetchOrderDetails();
      },
      (error: any) => setErrorData(error.response.data)
    );
  };

  return (
    <NxtbnModal size="sm" isOpen={isOpen} onClose={onClose}>
      <div className="p-4">
        <h2 className="border-b pb-3">Mark as Paid</h2>
        <p>Amount: {order?.humanizeTotalPrice}</p>
        <NXForm
          className="mt-6"
          onSubmit={onSubmit}
          nonFieldError={errorData}
          showErrorPopup={true}
          >
          {/* Transaction ID field */}
          <div className="w-full">
            <label
              htmlFor="transaction_id"
              className="block text-sm font-medium text-gray-700"
            >
              Transaction ID
            </label>
            <InputField
              id="transaction_id"
              type="text"
              name="transaction_id"
              value={formData?.transaction_id || ""}
              onChange={handleChange}
              placeholder="Enter transaction ID"
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              errorData={errorData}
            />
          </div>

          {/* Link for toggling advanced fields */}
          <button
            type="button"
            className="text-blue-600 text-sm mt-2"
            onClick={toggleAdvancedFields}
          >
            {showAdvanced ? "Hide Advanced" : "Show Advanced"}
          </button>

          {/* Advanced fields */}
          {showAdvanced && (
            <>
              <div className="flex flex-col md:flex-row gap-1 md:gap-3">
                <div className="mt-3 w-full">
                  <label
                    htmlFor="payment_amount"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Amount
                  </label>
                  <InputField
                    id="payment_amount"
                    type="number"
                    name="payment_amount"
                    value={formData?.payment_amount || ""}
                    onChange={handleChange}
                    placeholder="0.00"
                    className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    errorData={errorData}
                  />
                </div>
                <div className="mt-3 w-full">
                  <label
                    htmlFor="paid_at"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Paid At
                  </label>
                  <InputField
                    id="paid_at"
                    name="paid_at"
                    type="datetime-local"
                    value={moment(formData?.paid_at).format("YYYY-MM-DDTHH:mm")}
                    onChange={handleChange}
                    placeholder="Enter date and time"
                    className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    errorData={errorData}
                  />
                </div>
              </div>

              {/* Force It Checkbox */}
              <div className="mt-3 flex items-start">
                <div className="flex items-center h-5">
                  <InputField
                    id="force_it"
                    name="force_it"
                    type="checkbox"
                    checked={forceIt}
                    onChange={handleCheckboxChange}
                    className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="force_it" className="font-medium text-gray-700">
                    Force Mark It Paid
                  </label>
                  <p className="text-gray-500">
                    When you force it, nothing will be validated, the payment will
                    be created anyway.
                  </p>
                </div>
            </div>


            </>
          )}

          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={onClose}
              className="mr-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 hover:bg-primary-400 text-white rounded-md hover:bg-primary-700"
            >
              Create
            </button>
          </div>
        </NXForm>
      </div>
    </NxtbnModal>
  );
}

export default PaymentForm;
